import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Organization } from '../../../core/models/organization.model';
import { OrganizationService } from './organization.service';
import { ConcessionControl } from 'src/app/core/models/concession-control.model';
import { ConcessionControlStatus } from '../../main/concessioncontrol/assets/constants';
import { CracMapStatus } from '../../main/cracmap/assets/constants';
import { CracMap } from 'src/app/core/models/crac-map.model';

export enum StudyType {
    CC = 'cc',
    CRACMAP = 'cracmap',
    CRAC = 'crac',
}

/** This file is divided in 3 parts:
 * - The definition of the Store : defining data structure
 * - Constants that allow NgXs to trigger Actions
 * - Selectors & Actions : Functions allowed to interact with the store
 *
 */

// Store definition
export interface OrganizationStateModel {
    organizations: Organization[];
    createdOrganization: Organization | null;
    userOrganization: Organization | null;
    currentOrganization: Organization | null;
    organizationPart: Partial<Organization> | null;
    isLoading: boolean;
    concessionControlPart: Partial<ConcessionControl> | null;
    currentYear: number;
    currentStudyType: StudyType | null;
}

//Constants

export class GetAllOrganizations {
    static readonly type = 'ADMIN/ORGANIZATION/ALL';
}

export class DeleteOrganization {
    static readonly type = 'ADMIN/ORGANIZATION/DELETE';
    constructor(public payload: { organizationId: string }) {}
}

export class UpdateOrganization {
    static readonly type = 'ADMIN/ORGANIZATION/UPDATE';
    constructor(public payload: { organizationPart: Partial<Organization>; avatarFile?: File }) {}
}

export class CreateOrganization {
    static readonly type = 'ADMIN/ORGANIZATION/CREATE';
    constructor(public payload: { organization: Partial<Organization>; avatarFile?: File }) {}
}

export class CreateOrganizationSuccess {
    static readonly type = '[Organization] Create Organization Success';
    constructor(public payload: { organization: Organization }) {}
}

export class SetCurrentOrganization {
    static readonly type = 'ADMIN/ORGANIZATION/ID';
    constructor(public payload: { organizationId?: string; organization?: Organization }) {}
}

export class SetCurrentYear {
    static readonly type = 'ORGANIZATION/YEAR';
    constructor(public payload: { year: number }) {}
}

export class SetCurrentStudyType {
    static readonly type = 'ORGANIZATION/STUDY_TYPE';
    constructor(public payload: { studyType: StudyType | null }) {}
}

export class SetUserOrganization {
    static readonly type = 'ORGANIZATION/SET_USER_ORGANIZATION';
    constructor(public payload: { organization: Organization; isAdmin: boolean }) {}
}

export class UpdateConcessionControl {
    static readonly type = 'ADMIN/CONCESSIONCONTROL/ORGANIZATION/UPDATE';
    constructor(
        public payload: {
            concessionControlPart: Partial<ConcessionControl>;
            file?: File;
            isFileDeleted: boolean;
            isNewFile: boolean;
        },
    ) {}
}
export class UpdateCracMap {
    static readonly type = 'ADMIN/CRACMAP/ORGANIZATION/UPDATE';
    constructor(
        public payload: {
            cracMap: Partial<CracMap>;
        },
    ) {}
}

export class SynchronizeConcessionControl {
    static readonly type = 'ADMIN/CONCESSIONCONTROL/ORGANIZATION/SYNCHRONIZE';
    constructor(
        public payload: {
            year: number;
        },
    ) {}
}

export class SynchronizeCracMap {
    static readonly type = 'ADMIN/CRACMAP/SYNCHRONIZE';
    constructor(
        public payload: {
            year: number;
        },
    ) {}
}

//Actions

//Usual latest control is last year
const defaultCurrentYear = new Date().getFullYear() - 1;

@State<OrganizationStateModel>({
    name: 'Organizations',
    defaults: {
        organizations: [],
        createdOrganization: null,
        userOrganization: null,
        currentOrganization: null,
        organizationPart: null,
        isLoading: false,
        concessionControlPart: null,
        currentYear: defaultCurrentYear,
        currentStudyType: null,
    },
})
@Injectable()
export class OrganizationState {
    @Selector()
    public static organizations(state: OrganizationStateModel): Organization[] {
        return state.organizations;
    }

    @Selector()
    public static createdOrganization(state: OrganizationStateModel): Organization | null {
        return state.createdOrganization;
    }

    @Selector()
    public static userOrganization(state: OrganizationStateModel): Organization | null {
        return state.userOrganization;
    }

    @Selector()
    public static currentOrganization(state: OrganizationStateModel): Organization | null {
        return state.currentOrganization;
    }

    @Selector()
    public static currentYear(state: OrganizationStateModel): number {
        return state.currentYear;
    }

    @Selector()
    public static currentStudyType(state: OrganizationStateModel): StudyType | null {
        return state.currentStudyType;
    }

    @Selector()
    static isLoading(state: OrganizationStateModel): boolean {
        return state.isLoading;
    }

    constructor(
        private organizationService: OrganizationService,
        private readonly store: Store,
    ) {}

    @Action(GetAllOrganizations)
    public async getAllOrganizations(
        ctx: StateContext<OrganizationStateModel>,
        action: GetAllOrganizations,
    ): Promise<void> {
        ctx.patchState({ isLoading: true });

        const organizations = await this.organizationService.getAllOrganizations();
        organizations.map((organization) => this.addThisYearConcessionControl(organization));
        organizations.map((organization) => this.addThisYearCracMap(organization));

        ctx.patchState({ organizations: organizations, isLoading: false });
    }

    @Action(UpdateOrganization)
    public async updateOrganization(
        ctx: StateContext<OrganizationStateModel>,
        action: UpdateOrganization,
    ): Promise<void> {
        try {
            const updatedOrganization = await this.organizationService.updateOrganization(
                action.payload.organizationPart,
                action.payload.avatarFile,
            );
            const organizations = ctx.getState().organizations;
            const organizationIndex = organizations.findIndex(
                (organization) => organization.id === updatedOrganization.id,
            );
            organizations[organizationIndex] = updatedOrganization;
            ctx.patchState({ currentOrganization: updatedOrganization, organizations });
        } catch (err: any) {
            console.log(err);
            throw err;
        }
    }

    @Action(SynchronizeConcessionControl)
    public async synchronizeConcessionControl(
        ctx: StateContext<OrganizationStateModel>,
        action: SynchronizeConcessionControl,
    ): Promise<void> {
        const state = ctx.getState();
        const res = await this.organizationService.synchronizeConcessionControl(
            action.payload.year,
            state.currentOrganization?.id!,
        );
        if (res !== true) {
            throw new Error(res || 'Error');
        }
    }

    @Action(SynchronizeCracMap)
    public async synchronizeCracMap(
        ctx: StateContext<OrganizationStateModel>,
        action: SynchronizeCracMap,
    ): Promise<void> {
        const state = ctx.getState();
        const res = await this.organizationService.synchronizeCracMap(
            action.payload.year,
            state.currentOrganization?.id!,
        );
        if (res !== true) {
            throw new Error(res || 'Error');
        }
    }

    @Action(CreateOrganization)
    public async createOrganization(
        ctx: StateContext<OrganizationStateModel>,
        action: CreateOrganization,
    ): Promise<Organization> {
        try {
            const organization = await this.organizationService.createOrganization(
                action.payload.organization,
                action.payload.avatarFile,
            );
            this.addThisYearConcessionControl(organization);
            const currentState = ctx.getState();
            const currentOrganizations = currentState.organizations || [];
            const updatedOrganizations = [...currentOrganizations, organization];
            ctx.patchState({ organizations: updatedOrganizations });
            return organization;
        } catch (err: any) {
            console.error(err);
            throw err;
        }
    }

    @Action(DeleteOrganization)
    public async deleteOrganization(
        ctx: StateContext<OrganizationStateModel>,
        action: DeleteOrganization,
    ): Promise<void> {
        try {
            await this.organizationService.deleteOrganization(action.payload.organizationId);
            ctx.dispatch(GetAllOrganizations);
        } catch (err: any) {
            console.error('Error DeleteOrganization', err);
            throw err;
        }
    }

    @Action(SetCurrentOrganization)
    public async setCurrentOrganization(
        ctx: StateContext<OrganizationStateModel>,
        action: SetCurrentOrganization,
    ): Promise<void> {
        if (action.payload.organization) {
            ctx.patchState({ currentOrganization: action.payload.organization });
            return;
        }

        if (!action.payload.organizationId) {
            ctx.patchState({ currentOrganization: null });
            return;
        }

        const organizations = ctx.getState().organizations;
        let currentOrganization = organizations.find(
            (organization) => organization.id === action.payload.organizationId,
        );

        if (!currentOrganization) {
            currentOrganization = await this.organizationService.getOrganizationById(
                action.payload.organizationId,
            );
            this.addThisYearConcessionControl(currentOrganization);
            this.addThisYearCracMap(currentOrganization);
        }
        ctx.patchState({ currentOrganization });
    }

    @Action(SetCurrentYear)
    public async setCurrentYear(
        ctx: StateContext<OrganizationStateModel>,
        action: SetCurrentYear,
    ): Promise<void> {
        if (!action.payload.year) {
            return;
        }

        ctx.patchState({ currentYear: action.payload.year });
    }
    @Action(SetCurrentStudyType)
    public async setCurrentStudyType(
        ctx: StateContext<OrganizationStateModel>,
        action: SetCurrentStudyType,
    ): Promise<void> {
        if (!action.payload.studyType) {
            return;
        }

        ctx.patchState({ currentStudyType: action.payload.studyType });
    }

    @Action(SetUserOrganization)
    public async setUserOrganization(
        ctx: StateContext<OrganizationStateModel>,
        action: SetUserOrganization,
    ): Promise<void> {
        ctx.patchState({ userOrganization: action.payload.organization });

        if (!action.payload.isAdmin) {
            this.store.dispatch(
                new SetCurrentOrganization({ organization: action.payload.organization }),
            );
        }
    }

    @Action(UpdateConcessionControl)
    public async updateConcessionControl(
        ctx: StateContext<OrganizationStateModel>,
        action: UpdateConcessionControl,
    ): Promise<void> {
        try {
            const updatedConcessionControl = await this.organizationService.updateConcessionControl(
                action.payload.concessionControlPart,
            );

            const organizationId: string =
                action.payload.concessionControlPart.organization?.id ?? '';
            const file: File | undefined = action.payload.file;

            await this.organizationService.updateFileConcessionControl(
                organizationId,
                updatedConcessionControl.id,
                file!,
                action.payload.isFileDeleted,
                action.payload.isNewFile,
            );

            const state = ctx.getState();

            if (state.currentOrganization) {
                const updatedConcessionControls =
                    state.currentOrganization.concessionControls?.map((control) =>
                        ['new-study', updatedConcessionControl.id].includes(control.id)
                            ? updatedConcessionControl
                            : control,
                    ) || [];
                state.currentOrganization.concessionControls = updatedConcessionControls;

                ctx.patchState({
                    currentOrganization: { ...state.currentOrganization },
                });
            }
        } catch (err: any) {
            console.error('Error updating concession control:', err);
            throw err;
        }
    }

    private addThisYearConcessionControl(organization: Organization): void {
        if (!organization.concessionControls) {
            organization.concessionControls = [];
        }

        const lastYear = new Date().getFullYear() - 1;
        if (!organization.concessionControls.find((ccStudy) => ccStudy.year === lastYear)) {
            const newStudy: ConcessionControl = {
                id: 'new-study',
                year: lastYear,
                status: ConcessionControlStatus.AWAITING_THE_ANNUAL_ORDER,
                description: '',
                document: null,
                organization: {
                    id: organization.id,
                    name: '',
                    clientCode: '',
                    departmentCode: '',
                    userCount: 0,
                    concessionControls: [],
                    cracMaps: [],
                    cracs: [],
                },
                sizeFile: 0,
            };
            organization.concessionControls.push(newStudy);
        }
    }

    @Action(UpdateCracMap)
    public async updateCracMap(
        ctx: StateContext<OrganizationStateModel>,
        action: UpdateCracMap,
    ): Promise<void> {
        try {
            const updatedCracMap = await this.organizationService.updateCracMap(
                action.payload.cracMap,
            );

            const state = ctx.getState();

            if (state.currentOrganization) {
                const updateCracMaps =
                    state.currentOrganization.cracMaps?.map((control) =>
                        ['new-study', updatedCracMap.id].includes(control.id)
                            ? updatedCracMap
                            : control,
                    ) || [];
                state.currentOrganization.cracMaps = updateCracMaps;

                ctx.patchState({
                    currentOrganization: { ...state.currentOrganization },
                });
            }
        } catch (err: any) {
            console.error('Error updating crac map:', err);
            throw err;
        }
    }

    private addThisYearCracMap(organization: Organization): void {
        if (!organization.cracMaps) {
            organization.cracMaps = [];
        }
        const lastYear = new Date().getFullYear() - 1;
        if (!organization.cracMaps.find((ccStudy) => ccStudy.year === lastYear)) {
            const cracMap: CracMap = {
                id: 'new-study',
                year: lastYear,
                status: CracMapStatus.WAITING,
                organization: {
                    id: organization.id,
                    name: '',
                    clientCode: '',
                    departmentCode: '',
                    userCount: 0,
                    concessionControls: [],
                    cracMaps: [],
                    cracs: [],
                },
            };
            organization.cracMaps.push(cracMap);
        }
    }
}
